import { createContext, useCallback, useContext, useMemo } from 'react';
import usePath from '../../../../../../components/Hooks/usePath';
import { shouldDisableGridByProductColorGridFlag } from '../../../../../../utils/shouldDisableGridByProductColorGridFlag';
import { getOrderViewMode } from '../../../../contexts/order.utils';
import { OnGridValueChange } from '../../../interfaces/on-grid-value-change.interface';
import { UpdateItemFormat } from '../../../orderProducts.types';
import { buildParamsToUpdateItemFormat } from '../../../orderProducts.utils';
import { isInputWithError, shouldDisableProductInput } from '../../utils/productGrid.utils';
import {
  GridItemSkuIdsErrorAndDisabled,
  OrderProductGridContextProps,
  OrderProductGridContextProviderProps,
  orderProductGridContextInitialState,
} from '../types/OrderProductGridContext.types';

const OrderProductGridContext = createContext<OrderProductGridContextProps>(
  orderProductGridContextInitialState,
);

const arrayInitialStateToAvoidRerender: any[] = [];
export const OrderProductGridContextProvider = ({
  children,
  orderId,
  getLimit,
  orderStatus,
  onClickEnter,
  selectedPacks,
  hasValidPacks,
  getStockValue,
  suggestedPacks,
  stackOrderItem,
  removeCopiedGrid,
  createOrderItems,
  productSizeNames,
  showSuggestedGrid,
  handleSelectPacks,
  resetSelectedPacks,
  suggestionAchieved,
  productAddedByActions,
  selectPacksSideEffect,
  handleSelectSuggestion,
  onSelectPacksInstantAction,
  grid = arrayInitialStateToAvoidRerender,
  suggestedGrid = arrayInitialStateToAvoidRerender,
  packsWithError = arrayInitialStateToAvoidRerender,
  packsWithUnavailableStock = arrayInitialStateToAvoidRerender,
  readMode = false,
  disableAll = false,
  hideButtons = false,
  highlightProduct = false,
  handleClickPaste,
  copyGrid,
  copiedGrid,
  product,
  selloutSummary,
  validationErrors = arrayInitialStateToAvoidRerender,
}: OrderProductGridContextProviderProps) => {
  const { isPath } = usePath();

  const totalSuggestedGridItems = useMemo(
    () =>
      suggestedGrid?.reduce(
        (accumulator, suggestedGridItem) =>
          accumulator + (suggestedGridItem.orderItemQuantity ?? 0),
        0,
      ) ?? 0,
    [suggestedGrid],
  );

  const stackItem = useCallback(
    (item: UpdateItemFormat) => {
      stackOrderItem(item);
      if (selectedPacks.length > 0) {
        resetSelectedPacks();
      }
    },
    [resetSelectedPacks, selectedPacks.length, stackOrderItem],
  );

  const handleGridValueChange = useCallback(
    (params: OnGridValueChange) => {
      if (removeCopiedGrid) {
        removeCopiedGrid();
      }
      const updateItem = buildParamsToUpdateItemFormat(params, product.availability);
      stackItem(updateItem);
    },
    [stackItem, product.availability, removeCopiedGrid],
  );

  const orderViewMode = getOrderViewMode(isPath);

  const emptyGrid = grid.length === 0;
  const hasSuggestedGrid = totalSuggestedGridItems > 0;
  const hasSelectedPacks = selectedPacks.length > 0;

  const availableGrids = grid.filter((productGrid) => productGrid.available);
  const firstAvailableGrid = grid.find((item) => item.available);
  const lastAvailableGrid = availableGrids?.pop();
  const isOrderProductFromKit = grid.some((item) => !!item.orderItemKitId);
  const disableProductColorGrid = shouldDisableGridByProductColorGridFlag({
    availability: product.availability,
    allowChangeImmediateGrid: product.productAllowChangeImmediateGrid,
    allowChangePreOrderGrid: product.productAllowChangePreOrderGrid,
  });
  const shouldDisableOrderProductGrid =
    disableAll || disableProductColorGrid || isOrderProductFromKit;

  const gridItemSkuIdsErrorAndDisabled: GridItemSkuIdsErrorAndDisabled[] = useMemo(
    () =>
      grid.map((gridItem) => {
        const skuId = gridItem.skuId;
        const createOrderItem = createOrderItems?.find((coi) => gridItem.skuId === coi.skuId);

        const error = isInputWithError({
          availability: product.availability,
          availableForReadyForDeliveryWithoutStock:
            gridItem.availableForReadyForDeliveryWithoutStock,
          quantity: gridItem.orderItemQuantity || createOrderItem?.quantity,
          available: gridItem?.available,
          productSizeActive: !!gridItem?.productSizeActive,
          orderViewMode,
        });

        const disabled =
          shouldDisableOrderProductGrid ||
          readMode ||
          shouldDisableProductInput({
            error,
            gridItem,
            availability: product.availability,
            orderViewMode,
            createOrderItem,
            allowChangePreOrderGrid: product.productAllowChangePreOrderGrid,
            allowChangeImmediateGrid: product.productAllowChangeImmediateGrid,
          });

        return { skuId, error, disabled };
      }),
    [
      grid,
      readMode,
      orderViewMode,
      createOrderItems,
      product.availability,
      product.productAllowChangePreOrderGrid,
      product.productAllowChangeImmediateGrid,
      shouldDisableOrderProductGrid,
    ],
  );

  const value: OrderProductGridContextProps = {
    grid,
    orderId,
    readMode,
    getLimit,
    emptyGrid,
    disableAll: shouldDisableOrderProductGrid,
    orderStatus,
    hideButtons,
    onClickEnter,
    getStockValue,
    stackItem,
    orderViewMode,
    suggestedGrid,
    selectedPacks,
    hasValidPacks,
    suggestedPacks,
    packsWithError,
    hasSuggestedGrid,
    hasSelectedPacks,
    createOrderItems,
    productSizeNames,
    showSuggestedGrid,
    handleSelectPacks,
    lastAvailableGrid,
    firstAvailableGrid,
    suggestionAchieved,
    isOrderProductFromKit,
    productAddedByActions,
    handleGridValueChange,
    selectPacksSideEffect,
    handleSelectSuggestion,
    packsWithUnavailableStock,
    onSelectPacksInstantAction,
    highlightProduct,
    product,
    handleClickPaste,
    copyGrid,
    copiedGrid,
    removeCopiedGrid,
    selloutSummary,
    gridItemSkuIdsErrorAndDisabled,
    validationErrors,
  };

  return (
    <OrderProductGridContext.Provider value={value}>{children}</OrderProductGridContext.Provider>
  );
};
const useOrderProductGridContext = () =>
  useContext<OrderProductGridContextProps>(OrderProductGridContext);

export default useOrderProductGridContext;
